/**
 * The product drawer's Tags section (Phase C4): the API it reads and writes, and the pure rules
 * behind the editor — kept out of the component so they are unit-testable.
 *
 * GET  /inventory/:id/tags          the listing's tags (system with source, manual, suppressed, effective)
 * PATCH /inventory/:id/tags         manager+; `{ changes, expected }`, 409 PRODUCT_CHANGED on a conflict
 * GET  /inventory/tags/collections  the store's smart collections (rules), or why there are none
 * GET  /inventory/tags              every tag in the store (autocomplete; the Tags page)
 *
 * The pushed tags and the collection preview are recomputed here as the manager types, with the
 * same code the server runs (productTagRules / collectionRules are byte-for-byte the backend's).
 */

import { api } from '../../../utils/api'
import type { ListingFacts, SmartCollectionRules } from './collectionRules'
import { effectiveListingTags, normaliseTagList, sameTagList, tagMatchKey, tagProblem, type SourcedTag } from './productTagRules'

export interface ProductTagsData {
  system: SourcedTag[]
  manual: string[]
  suppressed: string[]
  effective: string[]
  productType: string
  vendor: string
  title: string
  listing: { rowCount: number; onListing: boolean }
  permissions?: { canEditTags: boolean }
}

export type SmartCollectionsData =
  | { status: 'ok'; collections: SmartCollectionRules[]; fetchedAt: string }
  | { status: 'not_connected' | 'unavailable'; collections: []; message: string }

export interface StoreTagsData {
  system: Array<{ tag: string; sources: string[]; products: number; suppressedOn: number }>
  manual: Array<{ tag: string; products: number; spellings: string[] }>
  productCount: number
  builtAt: string
  permissions: { canEditTags: boolean }
}

export const productTagsQueryKey = (productId: string) => ['inventory', 'product', productId, 'tags'] as const
export const smartCollectionsQueryKey = ['inventory', 'tags', 'collections'] as const
export const storeTagsQueryKey = ['inventory', 'tags', 'store'] as const

export async function fetchProductTags(productId: string, signal?: AbortSignal): Promise<ProductTagsData> {
  const response = await api.get(`/inventory/${productId}/tags`, { signal })
  return response.data.data as ProductTagsData
}

export async function fetchSmartCollections(signal?: AbortSignal): Promise<SmartCollectionsData> {
  const response = await api.get('/inventory/tags/collections', { signal })
  return response.data.data as SmartCollectionsData
}

export async function fetchStoreTags(signal?: AbortSignal): Promise<StoreTagsData> {
  const response = await api.get('/inventory/tags', { signal })
  return response.data.data as StoreTagsData
}

export interface TagsDraft {
  manual: string[]
  suppressed: string[]
}

/** The listing's pushed tags if the draft were saved. */
export const draftEffective = (data: Pick<ProductTagsData, 'system'>, draft: TagsDraft): string[] =>
  effectiveListingTags({ system: data.system, manual: draft.manual, suppressed: draft.suppressed })

/** What a collection rule can read about the listing, with these tags. */
export const listingFacts = (data: Pick<ProductTagsData, 'productType' | 'vendor' | 'title'>, tags: string[]): ListingFacts => ({
  tags,
  productType: data.productType,
  vendor: data.vendor,
  title: data.title,
})

/**
 * Add a typed tag to the manual list: refused with a reason when it is not a valid tag, is already
 * there (any case), or is already pushed as a system tag (then it would change nothing).
 */
export function addManualTag(
  data: Pick<ProductTagsData, 'system'>,
  draft: TagsDraft,
  raw: string,
): { ok: true; draft: TagsDraft } | { ok: false; error: string } {
  const problem = tagProblem(raw)
  if (problem) return { ok: false, error: problem }
  const [tag] = normaliseTagList([raw])
  const key = tagMatchKey(tag)
  if (draft.manual.some((t) => tagMatchKey(t) === key)) return { ok: false, error: `"${tag}" is already on this product.` }
  const suppressed = new Set(draft.suppressed.map(tagMatchKey))
  if (data.system.some((t) => tagMatchKey(t.tag) === key && !suppressed.has(key))) {
    return { ok: false, error: `"${tag}" is already a system tag on this product.` }
  }
  return { ok: true, draft: { ...draft, manual: [...draft.manual, tag] } }
}

export const removeManualTag = (draft: TagsDraft, tag: string): TagsDraft => ({
  ...draft,
  manual: draft.manual.filter((t) => tagMatchKey(t) !== tagMatchKey(tag)),
})

export function toggleSuppressed(draft: TagsDraft, tag: string): TagsDraft {
  const key = tagMatchKey(tag)
  const isSuppressed = draft.suppressed.some((t) => tagMatchKey(t) === key)
  return {
    ...draft,
    suppressed: isSuppressed ? draft.suppressed.filter((t) => tagMatchKey(t) !== key) : [...draft.suppressed, tag],
  }
}

export const isSuppressed = (draft: TagsDraft, tag: string): boolean =>
  draft.suppressed.some((t) => tagMatchKey(t) === tagMatchKey(tag))

/** Store tags matching what is typed, best first, leaving out what the product already has. */
export function tagSuggestions(store: StoreTagsData | undefined, typed: string, exclude: string[], limit = 8): string[] {
  if (!store) return []
  const needle = typed.trim().toLowerCase()
  const excluded = new Set(exclude.map(tagMatchKey))
  const seen = new Set<string>()
  const pool = [...store.manual, ...store.system]
    .filter((entry) => {
      const key = tagMatchKey(entry.tag)
      if (excluded.has(key) || seen.has(key)) return false
      seen.add(key)
      return !needle || entry.tag.toLowerCase().includes(needle)
    })
    .sort((a, b) => {
      const aStarts = a.tag.toLowerCase().startsWith(needle) ? 0 : 1
      const bStarts = b.tag.toLowerCase().startsWith(needle) ? 0 : 1
      return aStarts - bStarts || b.products - a.products || a.tag.localeCompare(b.tag)
    })
  return pool.slice(0, limit).map((entry) => entry.tag)
}

/** The PATCH body for what changed, or null when nothing did. */
export function buildTagsPayload(loaded: TagsDraft, draft: TagsDraft): {
  changes: Partial<TagsDraft>
  expected: Partial<TagsDraft>
} | null {
  const changes: Partial<TagsDraft> = {}
  const expected: Partial<TagsDraft> = {}
  if (!sameTagList(loaded.manual, draft.manual)) {
    changes.manual = draft.manual
    expected.manual = loaded.manual
  }
  if (!sameTagList(loaded.suppressed, draft.suppressed)) {
    changes.suppressed = draft.suppressed
    expected.suppressed = loaded.suppressed
  }
  return Object.keys(changes).length > 0 ? { changes, expected } : null
}

export interface TagsConflict {
  fields: Array<'manual' | 'suppressed'>
}

/** The 409 PRODUCT_CHANGED answer, or null for any other failure. */
export function readTagsConflict(err: unknown): TagsConflict | null {
  const response = (err as { response?: { status?: number; data?: { code?: string; details?: { fields?: unknown } } } })?.response
  if (response?.status !== 409 || response.data?.code !== 'PRODUCT_CHANGED') return null
  const fields = Array.isArray(response.data?.details?.fields)
    ? (response.data!.details!.fields as unknown[]).filter((f): f is 'manual' | 'suppressed' => f === 'manual' || f === 'suppressed')
    : []
  return { fields }
}
